import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '0', 10);
  const article = articles.find((a) => a.id === articleId);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
              <div className="w-16 h-16 mx-auto flex items-center justify-center">
                <i className="ri-file-search-line text-4xl text-foreground-400"></i>
              </div>
              <h1 className="mt-4 text-xl font-semibold text-foreground-900">文章未找到</h1>
              <p className="mt-2 text-sm text-foreground-600">您访问的文章不存在或已被移除</p>
              <Link
                to="/"
                className="mt-6 inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-sm"></i>
                </div>
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '黄页网站免费入口91',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://lc0001.cn/article/${article.id}`,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '如何在黄页网站免费入口91上免费收录企业信息？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您只需在黄页网站免费入口91首页点击"免费收录企业"按钮，填写公司名称、联系方式、经营范围等基本信息，提交后即可完成免费收录。',
        },
      },
      {
        '@type': 'Question',
        name: '黄页网站免费入口91收录企业信息需要收费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要。黄页网站免费入口91提供完全免费的企业信息收录服务，不收取任何费用，致力于为中小企业提供便捷的在线推广渠道。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <article>
            <div className="bg-background-950 text-white">
              <div className="max-w-4xl mx-auto px-4 md:px-6 py-14 md:py-20">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: article.categoryName, href: `/category/${article.category}` },
                    { label: article.title },
                  ]}
                />
                <div className="mt-6 flex items-center gap-3 text-xs text-background-400 flex-wrap">
                  <Link
                    to={`/category/${article.category}`}
                    className="text-accent-400 hover:text-accent-300 font-medium cursor-pointer whitespace-nowrap"
                  >
                    {article.categoryName}
                  </Link>
                  <span>·</span>
                  <span>{article.author}</span>
                  <span>·</span>
                  <span>{article.publishDate}</span>
                  <span>·</span>
                  <span className="flex items-center gap-1">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-eye-line text-xs"></i>
                    </div>
                    {article.readCount.toLocaleString()} 阅读
                  </span>
                </div>
                <h1 className="mt-4 text-2xl md:text-3xl font-bold font-heading leading-tight">
                  {article.title}
                </h1>
              </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 md:px-6 -mt-8">
              <div className="rounded-lg overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-64 md:h-80 object-cover object-top"
                />
              </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 md:px-6 py-10">
              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                  [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:font-heading
                  [&_p]:mb-4 [&_p]:leading-relaxed
                  [&_ul]:mb-4 [&_ul]:pl-5 [&_li]:mb-1 [&_li]:list-disc"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-8 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs text-secondary-700 bg-secondary-50 px-3 py-1 rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="bg-background-100 py-14">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="text-xl font-bold text-foreground-900 font-heading mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.id} article={ra} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}